import { useState, useEffect, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuthStore } from '@/stores/auth-store';
import { api } from '@/lib/api';

const DAILY_GOAL = 5;
const STORAGE_KEY = 'mw-daily-reads';

function getTodayKey(): string {
  return new Date().toISOString().slice(0, 10);
}

function getReadCount(): number {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return 0;
    const data = JSON.parse(raw) as { date: string; count: number };
    if (data.date !== getTodayKey()) return 0;
    return data.count;
  } catch {
    return 0;
  }
}

export interface StreakState {
  currentStreak: number;
  longestStreak: number;
  freezeTokensRemaining: number;
  dailyReads: number;
  dailyGoal: number;
  dailyProgress: number; // 0-100
  goalComplete: boolean;
}

export function useStreak(): StreakState {
  // T-127 tail: /api/users/me/streak 401s anonymously. Gate on the SAME field
  // the header and sidebar use, not a third derivation.
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const { data: streak } = useQuery({
    queryKey: ['streak'],
    queryFn: () => api.users.getStreak(),
    enabled: isAuthenticated,
  });

  const [dailyReads, setDailyReads] = useState(getReadCount);

  const refresh = useCallback(() => setDailyReads(getReadCount()), []);

  useEffect(() => {
    window.addEventListener('daily-read-update', refresh);
    return () => window.removeEventListener('daily-read-update', refresh);
  }, [refresh]);

  const progress = Math.min(dailyReads, DAILY_GOAL);

  return {
    currentStreak: streak?.currentStreak ?? 0,
    longestStreak: streak?.longestStreak ?? 0,
    freezeTokensRemaining: streak?.freezeTokensRemaining ?? 0,
    dailyReads,
    dailyGoal: DAILY_GOAL,
    dailyProgress: Math.round((progress / DAILY_GOAL) * 100),
    goalComplete: progress >= DAILY_GOAL,
  };
}
